import React, { useRef, useState, useEffect, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence, LayoutGroup } from 'motion/react';
import { useDraggable } from '@dnd-kit/core';
import { useSessionState } from '@/session';
import { isDesktop } from '@/lib/platform';
import { IconButton, Text, Flex, icon as iconSize, iconWeight, Dropdown, tabItem, Icon, Skeleton, useReducedMotion, Input } from '@nula/core-ui';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useEntityStore } from '@/lib/sync/entity-store';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import { ConversationBadge } from '@/features/assistant/components/conversation-badge';
import { useLiveRecording } from '@/lib/recordings/use-live-recording';
import { RecordingDot } from '@/components/recording-dot/recording-dot';
import type { DraggableTabData } from './tab-dnd';
import styles from './tab-bar.module.css';

// ── cva definitions ───────────────────────────────────────────────────────────

const tabLabel = cva('', {
  variants: {
    active: {
      true: styles.labelActive,
      false: styles.labelInactive,
    },
  },
  defaultVariants: { active: false },
});

// ── Tab icon helper ──────────────────────────────────────────────────────────

export function tabIcon(docType: string | undefined, viewId?: string) {
  // Named-view tabs carry no entity kind, so they must resolve their icon here —
  // otherwise they fall through to the docType switch and hit the 'description'
  // (document) fallback. `collections` uses the open-folder glyph to match how a
  // collection entity renders everywhere else; the rest mirror the sidebar nav.
  switch (viewId) {
    case 'home': return 'home';
    case 'documents': return 'auto_stories';
    case 'collections': return 'folder_open';
    case 'trash': return 'delete';
    case 'calendar': return 'calendar_month';
  }
  switch (docType) {
    case 'space': return 'workspaces';
    // document / canvas / collection / conversation (and any unknown kind)
    // resolve through the single source of truth so the tab icon can't drift
    // from the rest of the UI. Conversation → 'auto_awesome' (the Nula Chat
    // glyph) lives in entity-kind-meta.
    default: return getEntityKindMeta(docType).icon;
  }
}

// ── TabLabel ─────────────────────────────────────────────────────────────────

/** Map named view IDs to display labels */
const VIEW_LABELS: Record<string, string> = {
  home: 'Home',
  documents: 'Documents',
  collections: 'Collections',
  trash: 'Trash',
  calendar: 'Calendar',
};

/** Reads the live title from the workspace projection based on tab type. */
function TabLabel({ tabId, tabType, active }: { tabId: string; tabType?: string; active: boolean }) {
  const viewLabel = VIEW_LABELS[tabId];
  const skipDocLookup = tabType === 'collection' || tabType === 'conversation' || !!viewLabel;
  const projDoc = useWorkspaceDoc(skipDocLookup ? null : tabId);
  const allCollections = useWorkspaceCollections();
  // Conversation titles live in the entity store's conversations Map, not the
  // workspace-docs projection. Read it here so conversation tabs show their title.
  const conversationTitle = useEntityStore((s) =>
    tabType === 'conversation' ? s.conversations.get(tabId)?.title : undefined,
  );

  if (viewLabel) {
    return (
      <Text
        ellipsis
        className={active ? styles.labelViewActive : styles.labelViewInactive}
      >
        {viewLabel}
      </Text>
    );
  }

  // Conversation tab — title from the conversations Map. Falls back to 'New
  // conversation' while the entity row hasn't synced yet (a brand-new tab
  // that has not yet completed its first turn).
  if (tabType === 'conversation') {
    return (
      <>
        <Text ellipsis className={tabLabel({ active })}>
          {conversationTitle || 'New conversation'}
        </Text>
        <ConversationBadge conversationId={tabId} />
      </>
    );
  }

  // Collection tab — look up name from collection projection
  if (tabType === 'collection') {
    const col = allCollections.find((c) => c.id === tabId);
    const displayTitle = col?.name ?? 'Collection';
    return (
      <Text
        ellipsis
        className={tabLabel({ active })}
      >
        {displayTitle}
      </Text>
    );
  }

  const title = projDoc?.title;

  // Show skeleton while doc hasn't loaded yet (no projection entry, no type)
  if (!projDoc && !title) {
    return (
      <Skeleton
        title={{ width: 80 }}
        paragraph={false}
        active
        className={styles.skeleton}
      />
    );
  }

  // Collection fallback label (docType is 'document' | undefined, never 'collection')
  const displayTitle = title || 'Untitled';

  return (
    <Text
      ellipsis
      className={tabLabel({ active })}
    >
      {displayTitle}
    </Text>
  );
}

// ── TabDragGhost ─────────────────────────────────────────────────────────────

/**
 * Floating card rendered inside dnd-kit's DragOverlay while a tab is being
 * dragged. Shows the tab's icon (via tabIcon) and its resolved title (same
 * logic as TabLabel). Consumes design tokens exclusively — no hardcoded values.
 */
export function TabDragGhost({ tabId, tabType }: { tabId: string; tabType?: string }) {
  const viewLabel = VIEW_LABELS[tabId];
  const skipDocLookup = tabType === 'collection' || tabType === 'conversation' || !!viewLabel;
  const projDoc = useWorkspaceDoc(skipDocLookup ? null : tabId);
  const allCollections = useWorkspaceCollections();
  const conversationTitle = useEntityStore((s) =>
    tabType === 'conversation' ? s.conversations.get(tabId)?.title : undefined,
  );

  let title: string;
  if (viewLabel) {
    title = viewLabel;
  } else if (tabType === 'conversation') {
    title = conversationTitle || 'New conversation';
  } else if (tabType === 'collection') {
    const col = allCollections.find((c) => c.id === tabId);
    title = col?.name ?? 'Collection';
  } else {
    title = projDoc?.title || 'Untitled';
  }

  const icon = tabIcon(tabType, viewLabel ? tabId : undefined);

  return (
    <Flex align="center" gap="sm" className={styles.dragGhost}>
      <Icon name={icon} size={iconSize.sm} weight={iconWeight.light} />
      <Text ellipsis className={styles.dragGhostLabel}>{title}</Text>
    </Flex>
  );
}

// ── TabItem ──────────────────────────────────────────────────────────────────

function TabItem({
  tabId,
  tabType,
  panelId,
  activeId,
  tabIndex,
  onRenameDoc,
  onRenameCollection,
  onRenameConversation,
}: {
  tabId: string;
  tabType?: string;
  panelId: string;
  activeId: string;
  tabIndex: number;
  onRenameDoc?: (id: string, title: string) => void;
  onRenameCollection?: (id: string, name: string) => void;
  onRenameConversation?: (id: string, title: string) => void;
}) {
  const { openTab, closeTab } = useSessionState();
  const isView = tabId in VIEW_LABELS;
  const isConversation = tabType === 'conversation';
  const projDoc = useWorkspaceDoc(isView || isConversation ? '' : tabId);
  const canEdit = useCanEdit(isView ? null : tabId);
  const shouldReduce = useReducedMotion();
  const isRecording = useLiveRecording(isView ? null : tabId)?.phase === 'capturing';

  // Conversation titles live in the entity store's conversations Map, not the
  // workspace-docs projection — read it here so rename seeds + commits correctly.
  const conversationTitle = useEntityStore((s) =>
    isConversation ? s.conversations.get(tabId)?.title : undefined,
  );

  const active = activeId === tabId;
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const title = isView
    ? VIEW_LABELS[tabId]
    : isConversation
      ? conversationTitle || 'New Chat'
      : projDoc?.title || (tabType === 'collection' ? 'Collection' : 'Untitled');
  const [draft, setDraft] = useState(title);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabElRef = useRef<HTMLDivElement | null>(null);

  const draggableData: DraggableTabData = {
    type: 'tab',
    tabId,
    tabType,
    fromPanelId: panelId,
    fromTabIndex: tabIndex,
  };
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: `tab-${tabId}-${panelId}`,
    data: draggableData,
  });

  // Auto-scroll active tab into view
  useEffect(() => {
    if (active && tabElRef.current?.scrollIntoView) {
      tabElRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [active]);

  // Clean up click debounce timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const commitRename = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    setDraft(title);
    if (!trimmed || trimmed === title) return;

    if (isConversation) {
      // Conversations rename via REST PATCH (updateConversation) wired at the
      // app layer; the server marks the title sticky + emits a live update.
      onRenameConversation?.(tabId, trimmed);
    } else if (tabType === 'collection') {
      // For collection tabs, rename via callback (which updates Yjs directly)
      onRenameCollection?.(tabId, trimmed);
    } else {
      onRenameDoc?.(tabId, trimmed);
    }
  }, [draft, title, tabType, isConversation, onRenameDoc, onRenameCollection, onRenameConversation, tabId]);

  const handleClick = () => {
    if (editing) return;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      // Determine the tab type from what we already know. Conversation is
      // authoritative — passing 'document' here would let the session-reducer
      // clobber the type to 'document' and subscribe the tab to a non-existent
      // document room. The reducer also guards against this (defense-in-depth).
      const resolvedTabType = isView
        ? 'view' as const
        : tabType === 'collection'
          ? 'collection' as const
          : tabType === 'conversation'
            ? 'conversation' as const
            : 'document' as const;
      openTab(tabId, resolvedTabType, panelId);
    }, 200);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    // Named views and home are not renameable
    if (tabId === 'home' || tabId === 'documents' || tabId === 'collections' || tabId === 'trash' || tabId === 'calendar') return;
    // Conversations are private-by-default and owned by the viewer; the server
    // require_access-guards the rename PATCH, so the doc/collection canEdit
    // permission model doesn't apply. Shared docs/collections still gate on it.
    if (!isConversation && !canEdit) return;
    setDraft(title);
    setEditing(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeTab(tabId, panelId);
  };

  return (
    <motion.div
      ref={(el) => {
        tabElRef.current = el;
        setNodeRef(el);
      }}
      layout
      {...tabItem}
      className={styles.tabItemMotion}
      style={{ '--tab-opacity': isDragging ? 0.4 : 1 } as React.CSSProperties}
    >
      <Flex
        align="center"
        gap="sm"
        data-draggable-type="tab"
        data-tab-id={tabId}
        data-panel-id={panelId}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        // Disable drag while the inline rename input is active (PROD-2) — a
        // pointer-down inside the editable must not start a tab drag.
        {...(editing ? {} : listeners)}
        role="tab"
        aria-selected={active}
        tabIndex={active ? 0 : -1}
        className={clsx(
          styles.tabItemBase,
          active ? styles.tabItemActive : styles.tabItemInactive,
          !active && hovered && styles.tabItemHovered,
          isDragging && styles.tabItemDragging,
        )}
      >
        {active && (
          <motion.div
            layoutId={`tab-active-indicator-${panelId}`}
            className={styles.tabActiveIndicator}
            transition={shouldReduce ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
        <Icon
          name={tabIcon(projDoc?.kind ?? tabType, isView ? tabId : undefined)}
          size={iconSize.md}
          weight={iconWeight.light}
          className={active ? styles.tabIconActive : styles.tabIconInactive}
        />
        {isRecording && <RecordingDot />}
        {editing ? (
          <Input
            autoFocus
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { setDraft(title); setEditing(false); }
            }}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            className={styles.editInput}
          />
        ) : (
          <TabLabel tabId={tabId} tabType={tabType} active={active} />
        )}
        <IconButton
          size="sm"
          hoverBackground="color-mix(in srgb, var(--on-surface) 5%, transparent)"
          className={hovered ? styles.closeButtonVisible : styles.closeButtonHidden}
          onClick={handleClose}
          aria-label={`Close ${projDoc?.title || 'tab'}`}
        >
          <Icon name="close" size={iconSize.sm} weight={iconWeight.light} />
        </IconButton>
      </Flex>
    </motion.div>
  );
}

// ── ScrollableTabs ────────────────────────────────────────────────────────────

interface ScrollableTabsProps {
  children: React.ReactNode;
  onScrollStateChange: (state: { canScrollLeft: boolean; canScrollRight: boolean }) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

function ScrollableTabs({ children, onScrollStateChange, scrollRef }: ScrollableTabsProps) {
  const innerRef = useRef<HTMLDivElement>(null);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    onScrollStateChange({
      canScrollLeft: el.scrollLeft > 0,
      canScrollRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, [onScrollStateChange, scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    // Observe the inner content div — it grows as tabs are added
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(inner);
    // Also observe the scroll container for when the tabbar itself resizes
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, scrollRef]);

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(updateScrollState));
  }, [children, updateScrollState]);

  return (
    <Flex
      ref={scrollRef}
      align="center"
      className={styles.scrollContainer}
    >
      <Flex ref={innerRef} align="center" gap="md" className={styles.scrollInner}>
        {children}
      </Flex>
    </Flex>
  );
}

// ── ClosePanelButton ──────────────────────────────────────────────────────────

function ClosePanelButton({ onClick }: { onClick: () => void }) {
  return (
    <IconButton
      size="sm"
      hoverBackground="color-mix(in srgb, var(--error) 15%, transparent)"
      className={styles.closePanelButton}
      onClick={onClick}
      aria-label="Close panel"
    >
      <Icon name="close" size={iconSize.md} weight={iconWeight.light} />
    </IconButton>
  );
}

// ── TabBar ────────────────────────────────────────────────────────────────────

export interface TabBarProps {
  panelId: string;
  isFocused?: boolean;
  onSplit?: (direction: 'h' | 'v') => void;
  onClosePanel?: () => void;
  insertionIndex?: number;
  extraActions?: React.ReactNode;
  onRenameDoc?: (id: string, title: string) => void;
  onRenameCollection?: (id: string, name: string) => void;
  onRenameConversation?: (id: string, title: string) => void;
}

export function TabBar({ panelId, isFocused = false, onSplit, onClosePanel, insertionIndex, extraActions, onRenameDoc, onRenameCollection, onRenameConversation }: TabBarProps) {
  const { state } = useSessionState();
  const shouldReduce = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const panel = state?.panels[panelId];
  const activeId = panel?.activeId ?? 'home';
  const docTabs = (panel?.tabs ?? []).filter((tab) => tab.id !== 'home');
  const isVisible = (panel?.tabs?.length ?? 0) > 0 || !!onClosePanel || !!extraActions;

  const handleScrollStateChange = useCallback(({ canScrollLeft: l, canScrollRight: r }: { canScrollLeft: boolean; canScrollRight: boolean }) => {
    setCanScrollLeft(l);
    setCanScrollRight(r);
  }, []);

  const startScroll = (dir: 'left' | 'right') => {
    const step = () => {
      scrollRef.current?.scrollBy({ left: dir === 'left' ? -6 : 6 });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const stopScroll = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const handleTablistKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const tablist = e.currentTarget;
    const allTabs = Array.from(tablist.querySelectorAll<HTMLElement>('[role="tab"]'));
    const currentIndex = allTabs.findIndex((el) => el === document.activeElement);
    if (currentIndex === -1) return;
    e.preventDefault();
    const nextIndex = e.key === 'ArrowRight'
      ? (currentIndex + 1) % allTabs.length
      : (currentIndex - 1 + allTabs.length) % allTabs.length;
    allTabs[nextIndex]?.focus();
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  const leftScrollClass = !hasOverflow
    ? styles.scrollButtonHidden
    : canScrollLeft
      ? styles.scrollButtonActive
      : styles.scrollButtonInactive;

  const rightScrollClass = !hasOverflow
    ? styles.scrollButtonHidden
    : canScrollRight
      ? styles.scrollButtonActive
      : styles.scrollButtonInactive;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ maxHeight: 0, opacity: 0 }}
          animate={{ maxHeight: 80, opacity: 1 }}
          exit={{ maxHeight: 0, opacity: 0 }}
          transition={shouldReduce ? { duration: 0 } : { duration: 0.2 }}
          className={clsx('nula-tabbar', styles.tabbarOuter, !isFocused && styles.tabbarUnfocused)}
          data-slot="companion-perch-tabbar"
          data-companion-surface="perch"
        >
          <Flex
            align="center"
            gap="md"
            role="tablist"
            onKeyDown={handleTablistKeyDown}
            className={styles.tabbarRow}
            data-tauri-drag-region
            onMouseDown={(e) => {
              if (!isDesktop) return;
              if ((e.target as HTMLElement).closest('button, [role="tab"], input, a')) return;
              import('@tauri-apps/api/window').then(({ getCurrentWindow }) => getCurrentWindow().startDragging());
            }}
          >
            {/* LayoutGroup bridges layoutId handoff for the sliding active indicator
                across the AnimatePresence boundary. Without it, the indicator's
                shared-element animation gets blocked by AnimatePresence's mount/unmount
                lifecycle and cross-fades instead of sliding. */}
            <LayoutGroup id={`tabbar-${panelId}`}>
            <IconButton
              size="sm"
              pill
              className={leftScrollClass}
              onMouseDown={() => startScroll('left')}
              onMouseUp={stopScroll}
              onMouseLeave={stopScroll}
              aria-label="Scroll tabs left"
            >
              <Icon name="chevron_left" size={iconSize.md} weight={iconWeight.light} />
            </IconButton>
            <ScrollableTabs onScrollStateChange={handleScrollStateChange} scrollRef={scrollRef}>
              <AnimatePresence mode="popLayout">
                {docTabs.flatMap((tab, idx) => {
                  const items: React.ReactNode[] = [];
                  if (insertionIndex === idx) {
                    items.push(
                      <motion.div
                        key="tab-drop-spacer"
                        layout
                        data-testid="tab-drop-spacer"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 60, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={shouldReduce ? { duration: 0 } : { duration: 0.15 }}
                        className={styles.tabDropSpacer}
                      />,
                    );
                  }
                  items.push(
                    <TabItem key={tab.id} tabId={tab.id} tabType={tab.type} panelId={panelId} activeId={activeId} tabIndex={idx} onRenameDoc={onRenameDoc} onRenameCollection={onRenameCollection} onRenameConversation={onRenameConversation} />,
                  );
                  return items;
                })}
                {typeof insertionIndex === 'number' && insertionIndex >= docTabs.length && (
                  <motion.div
                    key="tab-drop-spacer"
                    layout
                    data-testid="tab-drop-spacer"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 60, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={shouldReduce ? { duration: 0 } : { duration: 0.15 }}
                    className={styles.tabDropSpacer}
                  />
                )}
              </AnimatePresence>
            </ScrollableTabs>
            </LayoutGroup>
            <IconButton
              size="sm"
              pill
              className={rightScrollClass}
              onMouseDown={() => startScroll('right')}
              onMouseUp={stopScroll}
              onMouseLeave={stopScroll}
              aria-label="Scroll tabs right"
            >
              <Icon name="chevron_right" size={iconSize.md} weight={iconWeight.light} />
            </IconButton>
            {onSplit && (
              <Dropdown
                menu={{
                  items: [
                    { key: 'h', label: 'Split right', icon: <Icon name="vertical_split" size={iconSize.md} weight={iconWeight.light} />, onClick: () => onSplit('h') },
                    { key: 'v', label: 'Split down', icon: <Icon name="horizontal_split" size={iconSize.md} weight={iconWeight.light} />, onClick: () => onSplit('v') },
                  ],
                }}
                trigger={['click']}
              >
                <IconButton
                  size="sm"
                  className={styles.splitButton}
                  aria-label="Split panel"
                >
                  <Icon name="vertical_split" size={iconSize.md} weight={iconWeight.light} />
                </IconButton>
              </Dropdown>
            )}
            {onClosePanel && <ClosePanelButton onClick={onClosePanel} />}
            {extraActions}
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
